import React from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { usePopup } from '@/context/PopupContext';
import { Check, Shield, Clock, Award, ArrowRight, CheckCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Pricing } from '@/components/sections/Pricing';
import { Testimonials } from '@/components/sections/Testimonials';

const serviceContent = {
  'trademark-registration': {
    title: 'Trademark Registration Services',
    subtitle: 'Protect your brand identity with comprehensive trademark registration',
    description: 'Secure exclusive rights to your brand name, logo, and slogan with our expert trademark registration services. We handle the entire process from comprehensive search to final registration.',
    backgroundImage: 'https://images.pexels.com/photos/8524942/pexels-photo-8524942.jpeg?auto=compress&cs=tinysrgb&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1664463760781-f159dfe3af30?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1769038947088-62455c949efc?q=85&w=800',
    aboutTitle: 'Comprehensive Trademark Protection',
    aboutDescription: 'Our trademark registration services provide complete brand protection through meticulous research, expert filing, and ongoing monitoring. We ensure your brand receives the strongest possible legal protection available.',
    aboutPoints: [
      'Comprehensive trademark search across federal and state databases',
      'Expert preparation of trademark applications',
      'Direct USPTO filing with expedited processing options',
      'Monitoring and response to office actions',
      'Final registration certificate and ongoing support'
    ],
    processSteps: [
      {
        title: 'Trademark Search',
        description: 'We conduct a thorough search to ensure your trademark is available and won\'t conflict with existing marks.'
      },
      {
        title: 'Application Filing',
        description: 'Our experts prepare and file your trademark application with the USPTO, ensuring accuracy and completeness.'
      },
      {
        title: 'Registration & Protection',
        description: 'Once approved, you receive your registration certificate and can enforce your exclusive trademark rights.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Trademark Services?',
    whyChooseDescription: 'With over a decade of experience and thousands of successful trademark registrations, we provide unmatched expertise and dedication to protecting your brand identity.',
    whyChoosePoints: [
      '98% Success Rate - Industry-leading approval rate',
      'Fast 24-Hour Filing - Get your application submitted quickly',
      'Expert Attorneys - Experienced trademark lawyers guide you',
      'Comprehensive Search - Thorough database screening',
      'Ongoing Support - We\'re here even after registration'
    ],
    faqs: [
      { question: 'How long does trademark registration take?', answer: 'The typical process takes 8-12 months from filing to registration, though you can begin using the TM symbol immediately after filing.' },
      { question: 'What can be trademarked?', answer: 'You can trademark brand names, logos, slogans, product names, and even specific colors or sounds associated with your brand.' },
      { question: 'Do I need a trademark attorney?', answer: 'While not required, working with an experienced attorney significantly increases your chances of approval and ensures proper protection.' },
      { question: 'How much does trademark registration cost?', answer: 'Our packages start at just $45 plus USPTO fees. We offer transparent pricing with no hidden costs.' }
    ],
    showPricing: true
  },
  'copyright-registration': {
    title: 'Copyright Registration Services',
    subtitle: 'Protect your creative works and intellectual property',
    description: 'Secure legal protection for your original works of authorship including books, music, artwork, software, and more. Our expert copyright registration services ensure your creative works are fully protected.',
    backgroundImage: 'https://images.unsplash.com/photo-1758520144420-3e5b22e9b9a4?q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1522542550221-31fd19575a2d?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=85&w=800',
    aboutTitle: 'Complete Copyright Protection',
    aboutDescription: 'Copyright registration provides essential legal protection for your creative works, establishing a public record of your ownership and enabling you to enforce your rights in court. We handle the entire registration process efficiently and professionally.',
    aboutPoints: [
      'Registration of literary, musical, and artistic works',
      'Software and digital content copyright protection',
      'Expedited processing options available',
      'Complete preparation and filing with U.S. Copyright Office',
      'Certificate of registration and legal documentation'
    ],
    processSteps: [
      {
        title: 'Work Review',
        description: 'We analyze your creative work and determine the best copyright classification for maximum protection.'
      },
      {
        title: 'Application Preparation',
        description: 'Our team prepares your copyright application with all required materials and documentation.'
      },
      {
        title: 'Registration Complete',
        description: 'You receive your official copyright registration certificate, establishing legal proof of ownership.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Copyright Services?',
    whyChooseDescription: 'We specialize in copyright law and have successfully registered thousands of creative works across all categories. Our expertise ensures your work receives proper classification and complete legal protection.',
    whyChoosePoints: [
      'Fast Processing - Quick turnaround on all applications',
      'All Work Types - Books, music, art, software, and more',
      'Expert Guidance - Specialists in copyright law',
      'Proper Classification - Ensuring maximum protection',
      'Affordable Rates - Competitive pricing with no hidden fees'
    ],
    faqs: [
      { question: 'What can be copyrighted?', answer: 'Original works of authorship including literary works, music, dramatic works, artwork, photographs, software code, architectural works, and more can be copyrighted.' },
      { question: 'How long does copyright protection last?', answer: 'For works created after 1978, copyright generally lasts for the life of the author plus 70 years. For works made for hire, protection lasts 95 years from publication or 120 years from creation, whichever is shorter.' },
      { question: 'Do I need to register to have copyright protection?', answer: 'Copyright exists automatically when you create an original work. However, registration provides significant legal advantages including the ability to sue for infringement and claim statutory damages.' },
      { question: 'How long does copyright registration take?', answer: 'Standard processing typically takes 3-6 months. We also offer expedited processing options for faster registration when needed.' }
    ],
    showPricing: false
  },
  'patent-services': {
    title: 'Patent Services',
    subtitle: 'Protect your innovations and inventions',
    description: 'Secure exclusive rights to your inventions with expert patent services. We handle utility patents, design patents, and provisional applications, guiding you through every step of the patent process.',
    backgroundImage: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1568057373106-63057e421d1c?q=85&w=800',
    aboutTitle: 'Comprehensive Patent Protection',
    aboutDescription: 'Our patent services provide complete protection for your innovations. From initial patentability searches to final patent grants, we ensure your inventions receive the strongest possible protection under U.S. patent law.',
    aboutPoints: [
      'Utility, design, and provisional patent applications',
      'Comprehensive prior art and patentability searches',
      'Expert patent drafting and claims preparation',
      'USPTO prosecution and office action responses',
      'International patent filing (PCT) support'
    ],
    processSteps: [
      {
        title: 'Patentability Search',
        description: 'We conduct thorough prior art searches to assess your invention\'s novelty and patentability before filing.'
      },
      {
        title: 'Patent Drafting & Filing',
        description: 'Our patent attorneys draft comprehensive applications and file them with the USPTO to protect your invention.'
      },
      {
        title: 'Grant & Protection',
        description: 'After successful examination, you receive your patent grant giving you exclusive rights to your invention.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Patent Services?',
    whyChooseDescription: 'With decades of combined experience in patent law and engineering, our team has successfully secured thousands of patents across diverse industries including software, mechanical, electrical, and biotechnology.',
    whyChoosePoints: [
      'Technical Expertise - Patent attorneys with engineering backgrounds',
      'High Success Rate - Proven track record of patent grants',
      'Comprehensive Service - From search to final grant',
      'Strategic Guidance - Maximizing your IP portfolio value',
      'Global Coverage - International patent filing support'
    ],
    faqs: [
      { question: 'What types of patents are available?', answer: 'There are three main types: Utility patents (for new processes, machines, or compositions), Design patents (for ornamental designs), and Plant patents (for new plant varieties). We handle all types.' },
      { question: 'How long does the patent process take?', answer: 'Patent prosecution typically takes 18-36 months from filing to grant, depending on the technology area and USPTO backlog. Provisional applications can be filed quickly to establish an early filing date.' },
      { question: 'How much does patent filing cost?', answer: 'Costs vary significantly based on invention complexity and patent type. Utility patents generally range from $8,000-$15,000 including attorney fees and USPTO fees. Contact us for a detailed quote for your invention.' },
      { question: 'What is a provisional patent application?', answer: 'A provisional application is a quick, lower-cost way to establish an early filing date and claim "patent pending" status. It gives you 12 months to file a full utility patent application.' }
    ],
    showPricing: false
  },
  'company-formation': {
    title: 'Company Formation Services',
    subtitle: 'Start your business with the right legal structure',
    description: 'Professional business formation services for LLCs, Corporations, and Non-Profit organizations. We handle all the paperwork, state filings, and compliance requirements to get your business started quickly and correctly.',
    backgroundImage: 'https://images.unsplash.com/photo-1664575602554-2087b04935a5?q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1590103514966-5e2a11c13e21?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=85&w=800',
    aboutTitle: 'Complete Business Formation',
    aboutDescription: 'Starting a business requires careful planning and proper legal structure. We guide you through entity selection, state registration, tax setup, and ongoing compliance to ensure your business is built on a solid legal foundation.',
    aboutPoints: [
      'LLC, C-Corp, S-Corp, and Non-Profit formation',
      'EIN (Employer Identification Number) registration',
      'Operating agreements and corporate bylaws',
      'State business registration and compliance',
      'Registered agent service included'
    ],
    processSteps: [
      {
        title: 'Entity Selection',
        description: 'We help you choose the right business structure (LLC, Corporation, etc.) based on your goals, tax situation, and liability needs.'
      },
      {
        title: 'Filing & Registration',
        description: 'We prepare and file all formation documents with your state and obtain your EIN from the IRS.'
      },
      {
        title: 'Ongoing Compliance',
        description: 'You receive all necessary corporate documents and guidance on annual compliance requirements to keep your business in good standing.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Formation Services?',
    whyChooseDescription: 'We\'ve helped thousands of entrepreneurs launch their businesses with confidence. Our comprehensive formation services cover everything from initial planning to ongoing compliance, ensuring your business starts strong.',
    whyChoosePoints: [
      'Fast Formation - Most filings completed within 1-2 weeks',
      'Expert Guidance - Help choosing the right entity type',
      'Complete Service - Filing, EIN, and compliance documents',
      'Registered Agent - Free first year included',
      'Affordable Pricing - Transparent fees with no surprises'
    ],
    faqs: [
      { question: 'What\'s the difference between LLC and Corporation?', answer: 'An LLC offers flexibility and pass-through taxation with fewer formalities. A Corporation (C-Corp or S-Corp) has a more rigid structure but may offer tax advantages and is required for raising venture capital. We help you decide based on your specific situation.' },
      { question: 'How long does company formation take?', answer: 'Most state filings are processed within 1-2 weeks. Expedited filing options are available in most states for faster processing (often 1-3 business days).' },
      { question: 'Do I need a registered agent?', answer: 'Yes, all LLCs and Corporations must have a registered agent in their state of formation. We provide registered agent service for the first year at no additional cost.' },
      { question: 'What ongoing requirements will my company have?', answer: 'Requirements vary by state and entity type but typically include annual reports, franchise tax filings, and maintaining proper corporate records. We provide detailed guidance on your specific compliance obligations.' }
    ],
    showPricing: false
  },
  'amazon-brand-registry': {
    title: 'Amazon Brand Registry Services',
    subtitle: 'Protect your products on Amazon with Brand Registry enrollment',
    description: 'Complete Amazon Brand Registry enrollment services including trademark registration and brand protection. Secure your Amazon listings, access powerful brand protection tools, and prevent counterfeiters from selling fake versions of your products.',
    backgroundImage: 'https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?q=85&w=800',
    aboutTitle: 'Complete Amazon Brand Registry Setup',
    aboutDescription: 'Amazon Brand Registry provides powerful tools to protect your brand and manage your product listings. We handle the entire enrollment process, from trademark registration to final Brand Registry approval, ensuring your products are fully protected on Amazon.',
    aboutPoints: [
      'Trademark registration required for Brand Registry',
      'Complete Brand Registry enrollment and setup',
      'Access to Amazon\'s brand protection tools',
      'Enhanced content and A+ Content capabilities',
      'Protection against counterfeiters and unauthorized sellers'
    ],
    processSteps: [
      {
        title: 'Trademark Registration',
        description: 'We first register your trademark with USPTO, which is required for Amazon Brand Registry enrollment.'
      },
      {
        title: 'Brand Registry Enrollment',
        description: 'Once your trademark is filed, we enroll your brand in Amazon Brand Registry and set up your account.'
      },
      {
        title: 'Brand Protection Active',
        description: 'You gain access to powerful brand protection tools, enhanced content features, and can report violations directly.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Amazon Brand Registry Service?',
    whyChooseDescription: 'We specialize in helping Amazon sellers protect their brands. With thousands of successful Brand Registry enrollments, we understand the specific requirements and can navigate the process efficiently.',
    whyChoosePoints: [
      'Complete Service - Trademark + Brand Registry together',
      'Fast Enrollment - Quick turnaround on applications',
      'Amazon Experts - Specialists in e-commerce brand protection',
      'Ongoing Support - Help with violations and enforcement',
      'Proven Success - Thousands of brands protected on Amazon'
    ],
    faqs: [
      { question: 'What is Amazon Brand Registry?', answer: 'Amazon Brand Registry is a program that provides brand owners with tools to protect their intellectual property, manage product listings, and access enhanced marketing features like A+ Content and Sponsored Brands.' },
      { question: 'Do I need a registered trademark?', answer: 'Yes, you must have an active registered trademark (or a pending application) to enroll in Amazon Brand Registry. We handle the trademark registration as part of our service.' },
      { question: 'How long does the process take?', answer: 'Trademark registration takes 8-12 months. However, you can enroll in Brand Registry once your trademark application is filed with the USPTO, which we can complete within 24 hours.' },
      { question: 'What benefits does Brand Registry provide?', answer: 'Benefits include proactive brand protection, ability to report violations, access to A+ Content, Sponsored Brands advertising, enhanced brand analytics, and more control over your product listings.' }
    ],
    showPricing: false
  },
  'trademark-search': {
    title: 'Trademark Search Services',
    subtitle: 'Comprehensive trademark availability search before filing',
    description: 'Reduce the risk of trademark rejection with our comprehensive trademark search services. We search federal, state, and common law databases to identify potential conflicts before you invest in filing.',
    backgroundImage: 'https://images.unsplash.com/photo-1763739530672-4aadafbd81ff?q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1758691736933-bb0f88fe2e0c?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1775163024488-e88e4a71179f?q=85&w=800',
    aboutTitle: 'Thorough Trademark Availability Analysis',
    aboutDescription: 'A comprehensive trademark search is essential before filing to identify potential conflicts that could result in rejection or legal disputes. Our detailed search reports help you make informed decisions about your trademark.',
    aboutPoints: [
      'Federal USPTO database search',
      'State trademark database search',
      'Common law and unregistered mark search',
      'Detailed search report with analysis',
      'Risk assessment and recommendations'
    ],
    processSteps: [
      {
        title: 'Search Request',
        description: 'You provide your desired trademark and the goods/services you plan to use it for.'
      },
      {
        title: 'Comprehensive Search',
        description: 'We search federal, state, and common law databases for identical or similar marks that could conflict.'
      },
      {
        title: 'Detailed Report',
        description: 'You receive a comprehensive report analyzing potential conflicts with our professional recommendations.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Trademark Search?',
    whyChooseDescription: 'Our trademark searches go beyond basic database queries. We provide expert analysis and clear recommendations to help you assess risk and make informed decisions.',
    whyChoosePoints: [
      'Comprehensive Coverage - Federal, state, and common law',
      'Expert Analysis - Not just data, but professional interpretation',
      'Fast Turnaround - Reports delivered within 2-3 business days',
      'Clear Recommendations - Easy-to-understand risk assessment',
      'Affordable Pricing - Detailed reports at competitive rates'
    ],
    faqs: [
      { question: 'Why do I need a trademark search?', answer: 'A trademark search identifies potential conflicts before you invest in filing. It can save you thousands of dollars and months of time by identifying issues early.' },
      { question: 'What does the search cover?', answer: 'We search the USPTO federal database, all 50 state trademark databases, and common law sources including business directories, domain names, and social media.' },
      { question: 'How long does a search take?', answer: 'Most comprehensive searches are completed within 2-3 business days. Expedited same-day searches are available for urgent needs.' },
      { question: 'What if you find conflicts?', answer: 'We provide a detailed analysis of any conflicts found, including severity assessment and recommendations. We can suggest modifications or alternative marks if needed.' }
    ],
    showPricing: false
  },
  'office-action-response': {
    title: 'Trademark Office Action Response',
    subtitle: 'Expert response to USPTO office actions and rejections',
    description: 'Overcome USPTO office actions with expert legal responses. Our experienced trademark attorneys analyze rejections and craft compelling responses to move your application forward.',
    backgroundImage: 'https://images.unsplash.com/photo-1619418602850-35ad20aa1700?q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1603796846097-bee99e4a601f?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1768839722927-df0ef3188f6d?q=85&w=800',
    aboutTitle: 'Professional Office Action Response',
    aboutDescription: 'Receiving an office action doesn\'t mean your trademark application is denied. Most office actions can be overcome with proper legal arguments and evidence. Our attorneys have extensive experience responding to all types of office actions.',
    aboutPoints: [
      'Expert analysis of office action reasoning',
      'Comprehensive legal response preparation',
      'Supporting evidence and documentation',
      'Direct filing with USPTO within deadline',
      'High success rate overcoming office actions'
    ],
    processSteps: [
      {
        title: 'Office Action Analysis',
        description: 'We thoroughly review your office action and identify the specific issues raised by the USPTO examining attorney.'
      },
      {
        title: 'Response Preparation',
        description: 'Our attorneys prepare a detailed legal response with arguments, evidence, and amendments as needed.'
      },
      {
        title: 'Filing & Follow-up',
        description: 'We file your response with the USPTO and monitor your application through to approval or final action.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Office Action Response Service?',
    whyChooseDescription: 'Office actions require legal expertise to overcome. Our trademark attorneys have successfully responded to thousands of office actions across all industries and rejection types.',
    whyChoosePoints: [
      'High Success Rate - Proven track record of overcoming rejections',
      'Fast Response - We act quickly to meet USPTO deadlines',
      'Experienced Attorneys - Specialists in trademark prosecution',
      'All Issue Types - Likelihood of confusion, descriptiveness, and more',
      'Clear Communication - We explain everything in plain English'
    ],
    faqs: [
      { question: 'What is a trademark office action?', answer: 'An office action is a letter from the USPTO examining attorney raising issues with your trademark application. It may cite likelihood of confusion with another mark, descriptiveness issues, or other technical problems.' },
      { question: 'How long do I have to respond?', answer: 'The USPTO typically gives you 6 months to respond to an office action. However, we recommend responding as quickly as possible to avoid delays and potential abandonment.' },
      { question: 'What is the success rate?', answer: 'With proper legal arguments and evidence, many office actions can be overcome. Our success rate varies by issue type, but we successfully overcome the majority of office actions we respond to.' },
      { question: 'What if my response is rejected?', answer: 'If your initial response receives a final rejection, you may have options to appeal or file a continuation application. We guide you through all available options.' }
    ],
    showPricing: false
  },
  'trademark-renewal': {
    title: 'Trademark Renewal Services',
    subtitle: 'Maintain your trademark registration with timely renewals',
    description: 'Never lose your trademark due to missed deadlines. We handle all trademark renewal filings including Sections 8, 9, 15, and post-registration maintenance to keep your trademark active.',
    backgroundImage: 'https://images.unsplash.com/photo-1633526543814-9718c8922b7a?q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1564846824194-346b7871b855?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?q=85&w=800',
    aboutTitle: 'Complete Trademark Maintenance',
    aboutDescription: 'Trademark registrations require periodic renewals and filings to maintain protection. Missing a deadline can result in cancellation of your registration. We manage all renewal deadlines and filings to ensure continuous protection.',
    aboutPoints: [
      'Section 8 Declaration of Use (between 5th-6th year)',
      'Section 9 Renewal (every 10 years)',
      'Section 15 Incontestability (between 5th-6th year)',
      'Automatic deadline monitoring and reminders',
      'Proper specimen preparation and filing'
    ],
    processSteps: [
      {
        title: 'Deadline Monitoring',
        description: 'We track your trademark registration dates and notify you well in advance of upcoming renewal deadlines.'
      },
      {
        title: 'Filing Preparation',
        description: 'We prepare all required declarations, specimens of use, and renewal documents for USPTO filing.'
      },
      {
        title: 'Filing & Confirmation',
        description: 'We file your renewal with the USPTO and confirm acceptance, ensuring continuous protection of your trademark.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Renewal Services?',
    whyChooseDescription: 'Trademark renewal deadlines are strict and missing them can result in loss of your registration. Our automated monitoring system ensures you never miss a critical deadline.',
    whyChoosePoints: [
      'Never Miss a Deadline - Automatic monitoring and reminders',
      'Complete Service - All renewal filings handled for you',
      'Proper Specimens - We ensure acceptable proof of use',
      'Multi-class Support - Handle renewals across all classes',
      'Cost Effective - Affordable rates with no surprises'
    ],
    faqs: [
      { question: 'When do I need to renew my trademark?', answer: 'Between the 5th and 6th year after registration (Section 8), then every 10 years after registration (Section 9). Section 15 Incontestability can be filed between the 5th and 6th year as well.' },
      { question: 'What happens if I miss a deadline?', answer: 'Missing the Section 8 deadline results in cancellation of your registration. There is a 6-month grace period with additional fees. Missing the Section 9 renewal results in expiration.' },
      { question: 'What is a specimen of use?', answer: 'A specimen shows how you\'re actually using your trademark in commerce. Acceptable specimens include product labels, packaging, website screenshots showing the mark with products, or service advertising.' },
      { question: 'Can you manage renewals for existing trademarks?', answer: 'Yes, we can take over renewal management for any existing trademark registration. We\'ll review your registration dates and set up monitoring for all future deadlines.' }
    ],
    showPricing: false
  },
  'business-dissolution': {
    title: 'Business Dissolution Services',
    subtitle: 'Professional dissolution and wind-down services',
    description: 'Properly close your business with complete dissolution services. We handle state filings, tax clearances, and all legal requirements to ensure clean closure of your LLC or Corporation.',
    backgroundImage: 'https://images.unsplash.com/photo-1727767579106-c30a4c1c74c5?q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1612053494739-770e137fd924?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=85&w=800',
    aboutTitle: 'Complete Business Wind-Down',
    aboutDescription: 'Properly dissolving your business is crucial to avoid ongoing fees, taxes, and potential personal liability. We ensure all legal requirements are met for a clean business closure.',
    aboutPoints: [
      'State dissolution filing and approval',
      'Tax clearance certificates',
      'Final tax returns and filings',
      'Cancellation of business licenses and permits',
      'Asset distribution documentation'
    ],
    processSteps: [
      {
        title: 'Dissolution Planning',
        description: 'We review your business structure and create a comprehensive dissolution plan addressing all legal requirements.'
      },
      {
        title: 'Filings & Clearances',
        description: 'We file dissolution documents with your state, obtain tax clearances, and handle all required notifications.'
      },
      {
        title: 'Final Closure',
        description: 'Once approved, you receive confirmation of dissolution and are no longer subject to ongoing fees or obligations.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Dissolution Services?',
    whyChooseDescription: 'Improper dissolution can leave you personally liable for business debts and subject to ongoing fees and taxes. We ensure complete, legally compliant closure.',
    whyChoosePoints: [
      'Complete Compliance - All state and federal requirements met',
      'Tax Clearance - Proper IRS and state tax closures',
      'Liability Protection - Minimize personal exposure',
      'Fast Processing - Most dissolutions completed in 2-4 weeks',
      'Expert Guidance - Navigate complex dissolution requirements'
    ],
    faqs: [
      { question: 'Why can\'t I just stop operating my business?', answer: 'Simply ceasing operations doesn\'t dissolve your legal entity. You\'ll continue to owe annual fees, taxes, and remain liable for business obligations. Formal dissolution is necessary.' },
      { question: 'How long does business dissolution take?', answer: 'Most dissolutions are completed within 2-4 weeks, though timing varies by state. Tax clearances may take additional time.' },
      { question: 'What about outstanding debts?', answer: 'Business dissolution doesn\'t eliminate debts. All debts should be paid or settled before dissolution. We can advise on proper debt handling procedures.' },
      { question: 'Will I need to file final tax returns?', answer: 'Yes, final federal and state tax returns are required. We coordinate with tax professionals to ensure all tax obligations are properly concluded.' }
    ],
    showPricing: false
  },
  'legal-consultation': {
    title: 'Legal Consultation Services',
    subtitle: 'Expert legal advice for your business and IP needs',
    description: 'Get professional legal guidance from experienced attorneys. Whether you need advice on business structure, IP strategy, contracts, or compliance, our consultation services provide the expertise you need.',
    backgroundImage: 'https://images.pexels.com/photos/7841469/pexels-photo-7841469.jpeg?auto=compress&cs=tinysrgb&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1760346546771-a81d986459ff?q=85&w=800',
    aboutTitle: 'Comprehensive Legal Guidance',
    aboutDescription: 'Access experienced legal counsel for all your business and intellectual property needs. Our consultation services provide strategic advice, risk assessment, and actionable recommendations.',
    aboutPoints: [
      'Business formation and structure planning',
      'Intellectual property strategy and portfolio review',
      'Contract review and negotiation support',
      'Compliance and regulatory guidance',
      'Dispute resolution and litigation support'
    ],
    processSteps: [
      {
        title: 'Initial Consultation',
        description: 'We discuss your situation, objectives, and specific legal questions in a detailed consultation session.'
      },
      {
        title: 'Analysis & Research',
        description: 'Our attorneys research applicable law, review documents, and analyze your options and potential outcomes.'
      },
      {
        title: 'Recommendations',
        description: 'You receive detailed written recommendations with clear action items and strategic guidance for moving forward.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Legal Consultation?',
    whyChooseDescription: 'Get direct access to experienced attorneys who understand business and intellectual property law. Our practical, business-focused advice helps you make informed decisions.',
    whyChoosePoints: [
      'Experienced Attorneys - Specialists in business and IP law',
      'Practical Advice - Real-world, actionable recommendations',
      'Multiple Practice Areas - Comprehensive legal expertise',
      'Flexible Options - Hourly consultations or package rates',
      'Confidential Service - Attorney-client privilege protection'
    ],
    faqs: [
      { question: 'What can I discuss in a legal consultation?', answer: 'Any business or legal matter including entity formation, contracts, IP strategy, employment issues, compliance, disputes, or general business advice.' },
      { question: 'How long is a consultation session?', answer: 'Standard consultations are 1 hour. Extended sessions and ongoing advisory relationships are available based on your needs.' },
      { question: 'Will my consultation be confidential?', answer: 'Yes, all consultations are protected by attorney-client privilege. Information shared remains completely confidential.' },
      { question: 'Can you review documents or contracts?', answer: 'Yes, document review is a common consultation service. We can review contracts, agreements, legal notices, and other documents, providing analysis and recommendations.' }
    ],
    showPricing: false
  },
  'legal-consultation-on-partnerships-and-llps': {
    title: 'Legal Consultation on Partnerships & LLPs',
    subtitle: 'Expert guidance for partnership and LLP formation and compliance',
    description: 'Get professional legal advice for forming and managing partnerships and Limited Liability Partnerships. Our experienced attorneys provide strategic guidance on structure, agreements, compliance, and dispute resolution.',
    backgroundImage: 'https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1758518729240-7162d07427b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1759310610325-2c7cb621e5e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    aboutTitle: 'Comprehensive Partnership & LLP Advisory',
    aboutDescription: 'Partnerships and LLPs require careful planning and proper legal documentation. Our consultation services help you navigate formation, operation, and compliance issues while protecting your interests and minimizing disputes.',
    aboutPoints: [
      'Partnership and LLP formation guidance',
      'Partnership agreement drafting and review',
      'Operating agreement preparation',
      'Compliance and regulatory advice',
      'Dispute resolution and partner negotiations'
    ],
    processSteps: [
      {
        title: 'Initial Consultation',
        description: 'We discuss your business goals, partner relationships, and specific legal questions about partnerships or LLPs.'
      },
      {
        title: 'Analysis & Recommendations',
        description: 'Our attorneys analyze your situation and provide strategic recommendations on structure, agreements, and risk management.'
      },
      {
        title: 'Implementation Support',
        description: 'We assist with documentation, filings, and ongoing compliance to ensure your partnership operates smoothly.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Partnership Consultation?',
    whyChooseDescription: 'Partnership issues require specialized legal expertise. Our attorneys have extensive experience in partnership law and understand the unique challenges of multi-owner businesses.',
    whyChoosePoints: [
      'Partnership Specialists - Deep expertise in partnership law',
      'Preventive Focus - Avoid disputes through proper planning',
      'Clear Agreements - Well-drafted partnership documents',
      'Dispute Resolution - Mediation and negotiation support',
      'Ongoing Advisory - Available as your business evolves'
    ],
    faqs: [
      { question: 'What is the difference between a partnership and an LLP?', answer: 'A general partnership offers simple operation but unlimited personal liability. An LLP (Limited Liability Partnership) provides liability protection similar to an LLC while maintaining partnership tax treatment and management flexibility.' },
      { question: 'Do I need a written partnership agreement?', answer: 'While not legally required in most states, a written partnership agreement is essential. It defines roles, profit sharing, decision-making, and dispute resolution procedures, preventing costly misunderstandings.' },
      { question: 'How are partnerships taxed?', answer: 'Partnerships are pass-through entities. The partnership files an informational return (Form 1065), but profits and losses pass through to partners who report them on personal tax returns.' },
      { question: 'Can you help resolve partnership disputes?', answer: 'Yes, we provide mediation and negotiation services for partnership disputes, helping partners find solutions that preserve the business relationship or facilitate fair dissolution if necessary.' }
    ],
    showPricing: false
  },
  'due-diligence': {
    title: 'Due Diligence Services',
    subtitle: 'Comprehensive business investigation and risk assessment',
    description: 'Thorough due diligence services for mergers, acquisitions, investments, and business transactions. We investigate financial, legal, operational, and IP aspects to identify risks and opportunities.',
    backgroundImage: 'https://images.unsplash.com/photo-1709734110054-3b0db38c959c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1762427354251-f008b64dbc32?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1753955900083-b62ee8d97805?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    aboutTitle: 'Comprehensive Due Diligence Investigation',
    aboutDescription: 'Due diligence is critical for informed business decisions. Our thorough investigation process uncovers potential risks, validates claims, and identifies opportunities before you commit to major transactions.',
    aboutPoints: [
      'Financial due diligence and analysis',
      'Legal compliance and litigation review',
      'Intellectual property audit and verification',
      'Operational assessment and risk identification',
      'Detailed due diligence report with recommendations'
    ],
    processSteps: [
      {
        title: 'Scope Definition',
        description: 'We work with you to define the scope of due diligence based on the transaction type and your specific concerns.'
      },
      {
        title: 'Investigation & Analysis',
        description: 'Our team conducts comprehensive investigation across financial, legal, IP, and operational areas.'
      },
      {
        title: 'Report & Recommendations',
        description: 'You receive a detailed report highlighting findings, risks, opportunities, and actionable recommendations.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Due Diligence Services?',
    whyChooseDescription: 'Our multidisciplinary team brings expertise across law, finance, and operations. We dig deep to uncover issues others might miss, giving you confidence in your business decisions.',
    whyChoosePoints: [
      'Comprehensive Approach - Financial, legal, and operational review',
      'Experienced Team - Attorneys, CPAs, and business analysts',
      'Thorough Investigation - We leave no stone unturned',
      'Clear Reporting - Easy-to-understand findings and recommendations',
      'Deal Support - Ongoing guidance through transaction completion'
    ],
    faqs: [
      { question: 'What is due diligence?', answer: 'Due diligence is a comprehensive investigation and analysis conducted before a business transaction to verify information, identify risks, and assess opportunities. It helps buyers and investors make informed decisions.' },
      { question: 'How long does due diligence take?', answer: 'Timeline varies based on transaction size and complexity. Small transactions may take 2-4 weeks, while large acquisitions can take 2-3 months. We provide estimated timelines during initial consultation.' },
      { question: 'What areas do you investigate?', answer: 'We conduct financial analysis, legal compliance review, IP audit, operational assessment, customer/vendor review, employee matters, and regulatory compliance examination.' },
      { question: 'What if you find problems?', answer: 'We provide detailed analysis of any issues found, including severity assessment and recommendations. This may include price renegotiation, required fixes, deal restructuring, or transaction termination if warranted.' }
    ],
    showPricing: false
  },
  'business-immigration-law': {
    title: 'Business Immigration Law Services',
    subtitle: 'Visa and immigration solutions for businesses and employees',
    description: 'Expert immigration law services for businesses hiring foreign talent and individuals seeking work visas. We handle H-1B, L-1, O-1, E-2, green cards, and all business immigration matters.',
    backgroundImage: 'https://images.unsplash.com/photo-1655722723123-c68bdb4f3ce0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1581656702382-9ae90e68e7b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1655722724451-0df658a2ab23?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    aboutTitle: 'Complete Business Immigration Solutions',
    aboutDescription: 'Navigating U.S. immigration law is complex. Our immigration attorneys specialize in business visas, helping companies hire international talent and individuals secure work authorization in the United States.',
    aboutPoints: [
      'H-1B specialty occupation visas',
      'L-1 intracompany transfer visas',
      'O-1 extraordinary ability visas',
      'E-2 investor and treaty trader visas',
      'Employment-based green cards (EB-1, EB-2, EB-3)'
    ],
    processSteps: [
      {
        title: 'Eligibility Assessment',
        description: 'We evaluate your situation and determine the most appropriate visa category and immigration strategy.'
      },
      {
        title: 'Petition Preparation',
        description: 'Our team prepares comprehensive petitions with all required documentation, supporting evidence, and legal arguments.'
      },
      {
        title: 'Filing & Follow-up',
        description: 'We file with USCIS, monitor case status, respond to requests for evidence, and guide you through to approval.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Immigration Services?',
    whyChooseDescription: 'Immigration law is constantly changing and highly technical. Our immigration attorneys stay current on all policy changes and have successfully handled thousands of visa petitions.',
    whyChoosePoints: [
      'Immigration Specialists - Dedicated business immigration practice',
      'High Approval Rate - Proven track record of successful petitions',
      'All Visa Types - Comprehensive experience across categories',
      'Employer Solutions - Compliance and I-9 support',
      'Fast Processing - Premium processing and expedite options'
    ],
    faqs: [
      { question: 'What is an H-1B visa?', answer: 'The H-1B is a nonimmigrant visa for foreign workers in specialty occupations requiring specialized knowledge and a bachelor\'s degree or higher. It\'s commonly used by tech companies, healthcare, finance, and other professional services.' },
      { question: 'How long does the visa process take?', answer: 'Processing times vary by visa type and service center. H-1B with premium processing takes 15 days. L-1 visa processing takes 2-4 months. Green cards can take 1-5+ years depending on category and country of origin.' },
      { question: 'Can you help with green cards?', answer: 'Yes, we handle all employment-based green card categories including EB-1 (extraordinary ability), EB-2 (advanced degree), EB-3 (skilled workers), and family-based applications.' },
      { question: 'What if my petition is denied?', answer: 'We provide analysis of denial reasons and explore all available options including appeals, motions to reopen/reconsider, or refiling with additional evidence.' }
    ],
    showPricing: false
  },
  'it-and-commercial-law': {
    title: 'IT & Commercial Law Services',
    subtitle: 'Legal solutions for technology and commercial transactions',
    description: 'Specialized legal services for IT companies and commercial transactions. We handle software licensing, SaaS agreements, data security, technology contracts, and all aspects of technology law.',
    backgroundImage: 'https://images.unsplash.com/photo-1654588833369-5174f4640cd2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1762279389045-110301edeecc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1645570990200-2701a49d45ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    aboutTitle: 'Comprehensive Technology Law Expertise',
    aboutDescription: 'Technology and commercial transactions require specialized legal knowledge. Our attorneys combine legal expertise with technical understanding to protect your interests in software, SaaS, cloud computing, and commercial deals.',
    aboutPoints: [
      'Software licensing and SaaS agreements',
      'Technology development and services contracts',
      'Data security and cybersecurity compliance',
      'Commercial transaction documentation',
      'Vendor and customer contract negotiation'
    ],
    processSteps: [
      {
        title: 'Requirements Analysis',
        description: 'We understand your technology business model, transaction structure, and specific legal requirements.'
      },
      {
        title: 'Document Preparation',
        description: 'Our team drafts or reviews agreements, ensuring proper IP protection, liability limitation, and compliance.'
      },
      {
        title: 'Negotiation & Execution',
        description: 'We support negotiations and finalize documentation to protect your interests and facilitate successful deals.'
      }
    ],
    whyChooseTitle: 'Why Choose Our IT & Commercial Law Services?',
    whyChooseDescription: 'Technology law requires both legal expertise and technical knowledge. Our attorneys understand software, cloud services, cybersecurity, and technology business models.',
    whyChoosePoints: [
      'Technology Expertise - Attorneys with technical backgrounds',
      'Industry Experience - Deep knowledge of IT sector practices',
      'Comprehensive Services - Contracts, compliance, and IP protection',
      'Practical Approach - Business-focused solutions',
      'Fast Turnaround - Quick response for time-sensitive deals'
    ],
    faqs: [
      { question: 'What is a SaaS agreement?', answer: 'A Software as a Service (SaaS) agreement governs cloud-based software access. It covers service levels, data ownership, security, pricing, and termination. Proper SaaS agreements protect both providers and customers.' },
      { question: 'Do I need a software license agreement?', answer: 'Yes, if you develop or distribute software. License agreements define permitted use, restrictions, IP ownership, warranties, and liability limits. They\'re essential for protecting your software and limiting legal exposure.' },
      { question: 'What about data security compliance?', answer: 'We advise on data security regulations including GDPR, CCPA, HIPAA, and cybersecurity frameworks. We help implement compliant practices and draft appropriate privacy policies and data processing agreements.' },
      { question: 'Can you review vendor contracts?', answer: 'Yes, we review and negotiate vendor contracts for software, cloud services, IT consulting, and technology procurement. We ensure favorable terms and identify problematic clauses.' }
    ],
    showPricing: false
  },
  'data-protection-and-privacy-law': {
    title: 'Data Protection & Privacy Law Services',
    subtitle: 'GDPR, CCPA, and comprehensive privacy compliance',
    description: 'Expert guidance on data protection regulations and privacy compliance. We help businesses navigate GDPR, CCPA, HIPAA, and other privacy laws while implementing robust data protection practices.',
    backgroundImage: 'https://images.unsplash.com/photo-1614064746579-4918e0ef6e9c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1643962577583-a4c880a109ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1769029265788-d7921a103403?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    aboutTitle: 'Complete Privacy Compliance Solutions',
    aboutDescription: 'Data privacy regulations are complex and constantly evolving. Our privacy law experts help you achieve and maintain compliance with GDPR, CCPA, HIPAA, and other regulations while building customer trust.',
    aboutPoints: [
      'GDPR and CCPA compliance programs',
      'Privacy policy and notice drafting',
      'Data processing agreements (DPAs)',
      'Privacy impact assessments',
      'Data breach response and notification'
    ],
    processSteps: [
      {
        title: 'Privacy Audit',
        description: 'We assess your current data practices, identify compliance gaps, and evaluate risk exposure under applicable regulations.'
      },
      {
        title: 'Program Implementation',
        description: 'We develop and implement comprehensive privacy programs including policies, procedures, and documentation.'
      },
      {
        title: 'Ongoing Compliance',
        description: 'We provide ongoing monitoring, updates, training, and support to maintain compliance as regulations evolve.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Privacy Law Services?',
    whyChooseDescription: 'Privacy law requires specialized expertise and constant vigilance. Our privacy attorneys stay current on all regulatory developments and provide practical compliance solutions.',
    whyChoosePoints: [
      'Privacy Specialists - Dedicated data protection practice',
      'Global Compliance - GDPR, CCPA, PIPEDA, and more',
      'Practical Solutions - Business-friendly compliance programs',
      'Incident Response - 24/7 breach response support',
      'Training & Support - Employee privacy training programs'
    ],
    faqs: [
      { question: 'What is GDPR?', answer: 'The General Data Protection Regulation (GDPR) is EU law governing personal data processing. It applies to any business offering goods/services to EU residents or monitoring their behavior, regardless of business location.' },
      { question: 'What is CCPA?', answer: 'The California Consumer Privacy Act (CCPA) gives California residents rights over their personal information. It applies to businesses meeting threshold requirements that collect California residents\' data.' },
      { question: 'Do I need a privacy policy?', answer: 'Yes, virtually all websites and apps collecting user data need a privacy policy. Many laws require it, and it\'s essential for transparency and building customer trust.' },
      { question: 'What if we have a data breach?', answer: 'We provide immediate incident response including breach assessment, regulatory notification, customer communication, and remediation guidance. Speed is critical in breach situations.' }
    ],
    showPricing: false
  },
  'certificate-of-good-standing': {
    title: 'Certificate of Good Standing Services',
    subtitle: 'Official state certification of business compliance',
    description: 'Obtain Certificates of Good Standing (Certificate of Status/Existence) from any state. Required for bank loans, business transactions, foreign qualification, and contract bids.',
    backgroundImage: 'https://images.unsplash.com/photo-1589330694653-ded6df03f754?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1586864387507-c7761c02bc37?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1616101001234-7320af4f1aa7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    aboutTitle: 'Fast Certificate of Good Standing Service',
    aboutDescription: 'A Certificate of Good Standing verifies your business is legally formed, current with state filings, and authorized to operate. It\'s required for many business transactions and demonstrates compliance.',
    aboutPoints: [
      'Certificates from all 50 states',
      'Fast processing and expedited options',
      'Compliance verification before ordering',
      'Certified and apostilled copies available',
      'Multiple copies for different purposes'
    ],
    processSteps: [
      {
        title: 'Compliance Check',
        description: 'We verify your business is current with all state filings and requirements before requesting the certificate.'
      },
      {
        title: 'Certificate Request',
        description: 'We order your Certificate of Good Standing from the appropriate state agency with expedited processing if needed.'
      },
      {
        title: 'Delivery',
        description: 'You receive your official certificate, suitable for bank requirements, contracts, or foreign qualification filings.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Certificate Services?',
    whyChooseDescription: 'Getting a Certificate of Good Standing requires current compliance. We verify requirements, bring your business current if needed, and obtain certificates quickly.',
    whyChoosePoints: [
      'All States - Certificates from any U.S. state or territory',
      'Fast Service - Expedited processing available',
      'Compliance Expertise - We ensure you\'re current first',
      'Certified Copies - Official certification and apostille services',
      'Volume Discounts - Reduced rates for multiple certificates'
    ],
    faqs: [
      { question: 'What is a Certificate of Good Standing?', answer: 'A Certificate of Good Standing (also called Certificate of Status or Certificate of Existence) is an official state document verifying your business is properly formed, current with filings, and authorized to operate.' },
      { question: 'When do I need this certificate?', answer: 'Common situations include bank loan applications, business acquisitions, contract bids, foreign qualification, franchise agreements, and vendor applications. Many organizations require recent certificates (within 30-90 days).' },
      { question: 'What if my business is not in good standing?', answer: 'We identify what\'s needed to restore good standing (often past due reports or fees), help you file required documents, and then obtain your certificate once compliant.' },
      { question: 'How long does it take?', answer: 'Standard processing varies by state (3-10 business days typically). Expedited options are available in most states for 1-2 day turnaround.' }
    ],
    showPricing: false
  },
  'tax-advisory-and-filing': {
    title: 'Tax Advisory & Filing Services',
    subtitle: 'Strategic tax planning and compliance for businesses',
    description: 'Comprehensive tax advisory and filing services for businesses and individuals. We provide strategic tax planning, compliance support, IRS representation, and year-round tax guidance.',
    backgroundImage: 'https://images.unsplash.com/photo-1772588627499-baefc8ab0ce7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1775363949851-9b05cd14a67d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1772588627373-729b0f47e5bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    aboutTitle: 'Complete Tax Planning & Compliance',
    aboutDescription: 'Effective tax management requires both compliance and strategic planning. Our tax professionals help minimize tax liability while ensuring full compliance with federal, state, and local tax requirements.',
    aboutPoints: [
      'Business and individual tax return preparation',
      'Strategic tax planning and optimization',
      'Sales tax compliance and nexus analysis',
      'IRS audit representation and response',
      'Multi-state tax compliance'
    ],
    processSteps: [
      {
        title: 'Tax Assessment',
        description: 'We review your financial situation, business structure, and tax obligations to identify opportunities and risks.'
      },
      {
        title: 'Planning & Strategy',
        description: 'We develop tax strategies to minimize liability, optimize deductions, and ensure compliance across all jurisdictions.'
      },
      {
        title: 'Preparation & Filing',
        description: 'We prepare and file all required tax returns accurately and on time, with ongoing advisory support throughout the year.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Tax Advisory Services?',
    whyChooseDescription: 'Tax laws are complex and constantly changing. Our tax professionals combine technical expertise with strategic thinking to optimize your tax position while maintaining compliance.',
    whyChoosePoints: [
      'Expert Tax Professionals - CPAs and tax attorneys',
      'Proactive Planning - Not just compliance, but optimization',
      'Multi-State Expertise - Complex nexus and apportionment',
      'IRS Representation - Experienced audit defense',
      'Year-Round Support - Available beyond tax season'
    ],
    faqs: [
      { question: 'What types of tax returns do you prepare?', answer: 'We prepare business returns (C-Corp, S-Corp, Partnership, LLC), individual returns (Form 1040), trust and estate returns, sales tax returns, and payroll tax filings.' },
      { question: 'Can you help with tax planning?', answer: 'Yes, tax planning is central to our service. We help with entity selection, timing strategies, deduction maximization, retirement planning, and multi-year tax projections.' },
      { question: 'What if I\'m being audited by the IRS?', answer: 'We provide full IRS representation including audit response, documentation gathering, negotiation with IRS agents, and appeals if necessary. Our goal is to minimize additional tax and penalties.' },
      { question: 'Do you handle sales tax?', answer: 'Yes, we provide sales tax compliance including nexus analysis, registration, return preparation, voluntary disclosure agreements, and audit defense for businesses selling across multiple states.' }
    ],
    showPricing: false
  },
  'cfo-services': {
    title: 'CFO Services',
    subtitle: 'Fractional and outsourced CFO solutions for growing businesses',
    description: 'Expert CFO services for businesses that need strategic financial leadership without a full-time CFO. We provide financial strategy, cash flow management, forecasting, and executive-level financial guidance.',
    backgroundImage: 'https://images.unsplash.com/photo-1771244670407-42e9585ab333?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1666866834805-8cc91d4774ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1775046318523-b5227f41fe97?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    aboutTitle: 'Strategic Financial Leadership',
    aboutDescription: 'Growing businesses need CFO-level expertise but may not be ready for a full-time executive. Our fractional CFO services provide strategic financial leadership, planning, and execution at a fraction of full-time cost.',
    aboutPoints: [
      'Strategic financial planning and analysis',
      'Cash flow management and forecasting',
      'Financial reporting and KPI dashboards',
      'Fundraising support and investor relations',
      'Budget development and variance analysis'
    ],
    processSteps: [
      {
        title: 'Financial Assessment',
        description: 'We analyze your current financial situation, systems, and processes to identify opportunities and priorities.'
      },
      {
        title: 'Strategy Development',
        description: 'We develop comprehensive financial strategies aligned with your business goals, including planning, systems, and metrics.'
      },
      {
        title: 'Ongoing Partnership',
        description: 'We serve as your strategic financial partner, providing regular guidance, reporting, and executive-level financial leadership.'
      }
    ],
    whyChooseTitle: 'Why Choose Our CFO Services?',
    whyChooseDescription: 'Our experienced CFOs have led finance for companies from startups to multi-million dollar enterprises. We bring Fortune 500 expertise to growing businesses at an affordable price point.',
    whyChoosePoints: [
      'Experienced CFOs - Former executives from major companies',
      'Flexible Engagement - Part-time, project-based, or ongoing',
      'Strategic Focus - Not just accounting, but growth strategy',
      'Fundraising Expertise - Support for capital raises',
      'Cost Effective - CFO expertise without executive salary'
    ],
    faqs: [
      { question: 'What is a fractional CFO?', answer: 'A fractional CFO provides CFO-level financial leadership on a part-time or contract basis. You get strategic financial guidance and executive expertise without the cost of a full-time CFO salary and benefits.' },
      { question: 'What services are included?', answer: 'Services typically include financial planning and analysis, cash flow management, financial reporting, budgeting, forecasting, fundraising support, and strategic financial guidance. We customize based on your needs.' },
      { question: 'How much time commitment is required?', answer: 'Engagement levels vary based on business size and needs. Typical arrangements range from 10-40 hours per month. We can scale up or down as your business evolves.' },
      { question: 'Can you help with raising capital?', answer: 'Yes, we provide comprehensive fundraising support including financial modeling, investor presentations, due diligence preparation, term sheet negotiation, and investor relations.' }
    ],
    showPricing: false
  },
  'default': {
    title: 'Professional Legal Services',
    subtitle: 'Expert guidance for your intellectual property needs',
    description: 'Our experienced team provides comprehensive legal services to protect and enhance your intellectual property portfolio.',
    backgroundImage: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=85&w=1920',
    aboutImage: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?q=85&w=800',
    whyChooseImage: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=85&w=800',
    aboutTitle: 'Professional Legal Expertise',
    aboutDescription: 'We provide comprehensive legal services tailored to your specific intellectual property needs with a focus on quality, efficiency, and client satisfaction.',
    aboutPoints: [
      'Comprehensive legal research and analysis',
      'Expert document preparation and filing',
      'Direct communication with regulatory bodies',
      'Ongoing monitoring and support',
      'Proven track record of success'
    ],
    processSteps: [
      {
        title: 'Consultation',
        description: 'We begin with a thorough consultation to understand your specific needs and goals.'
      },
      {
        title: 'Preparation & Filing',
        description: 'Our team prepares all necessary documents and files them with the appropriate authorities.'
      },
      {
        title: 'Completion & Support',
        description: 'Once completed, we provide ongoing support and guidance to ensure continued protection.'
      }
    ],
    whyChooseTitle: 'Why Choose Our Services?',
    whyChooseDescription: 'With years of experience and a commitment to excellence, we deliver results that exceed expectations.',
    whyChoosePoints: [
      'Experienced Legal Team - Decades of combined experience',
      'Transparent Pricing - No hidden fees or surprises',
      'Fast Processing - Expedited services available',
      'Personal Service - Dedicated support throughout',
      'Proven Results - Thousands of successful clients'
    ],
    faqs: [
      { question: 'How do I get started?', answer: 'Simply fill out our contact form or call us to schedule a free consultation with one of our specialists.' },
      { question: 'What are the costs involved?', answer: 'Costs vary depending on the specific service. Contact us for a detailed quote tailored to your needs.' },
      { question: 'How long does the process take?', answer: 'Timelines vary by service type. We provide estimated timeframes during your initial consultation.' }
    ],
    showPricing: false
  }
};

export const ServicePage = () => {
  const { category, service } = useParams();
  const { openPopup } = usePopup();
  
  // Map URL category to content key
  const serviceKey = category === 'trademark-registration' ? 'trademark-registration'
                   : category === 'copyright-registration' ? 'copyright-registration'
                   : category === 'patent-services' ? 'patent-services'
                   : category === 'company-formation' ? 'company-formation'
                   : category === 'amazon-brand-registry' ? 'amazon-brand-registry'
                   : category === 'trademark-search' ? 'trademark-search'
                   : category === 'office-action-response' ? 'office-action-response'
                   : category === 'trademark-renewal' ? 'trademark-renewal'
                   : category === 'business-dissolution' ? 'business-dissolution'
                   : category === 'legal-consultation' ? 'legal-consultation'
                   : category === 'legal-services' && service === 'legal-consultation-on-partnerships-and-llps' ? 'legal-consultation-on-partnerships-and-llps'
                   : category === 'legal-services' && service === 'due-diligence' ? 'due-diligence'
                   : category === 'legal-services' && service === 'business-immigration-law' ? 'business-immigration-law'
                   : category === 'legal-services' && service === 'it-and-commercial-law' ? 'it-and-commercial-law'
                   : category === 'legal-services' && service === 'data-protection-and-privacy-law' ? 'data-protection-and-privacy-law'
                   : category === 'additional-services' && service === 'certificate-of-good-standing' ? 'certificate-of-good-standing'
                   : category === 'additional-services' && service === 'tax-advisory-and-filing' ? 'tax-advisory-and-filing'
                   : category === 'additional-services' && service === 'cfo-services' ? 'cfo-services'
                   : 'default';
  
  const content = serviceContent[serviceKey] || serviceContent.default;
  
  const formattedTitle = service 
    ? service.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    : content.title;

  return (
    <div className="min-h-screen">
      {/* Spacer for fixed navbar */}
      <div className="h-[116px]"></div>
      
      {/* 1. Hero Banner */}
      <section 
        className="relative py-20 md:py-32 overflow-hidden"
        style={{
          backgroundImage: `url('${content.backgroundImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/90 to-brand-navy/85" />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight">
              {formattedTitle}
            </h1>
            <p className="text-base md:text-lg leading-relaxed mb-8 text-gray-100 max-w-3xl mx-auto">
              {content.description}
            </p>
            <Button
              onClick={() => openPopup(serviceKey)}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium text-lg px-8 py-6 transition-transform hover:scale-105 shadow-xl"
            >
              Get Started Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* 2. About Service (Image on left) */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image on Left */}
            <div className="order-2 lg:order-1">
              <img
                src={content.aboutImage}
                alt={content.aboutTitle}
                className="rounded-2xl shadow-2xl w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
            
            {/* Content on Right */}
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-brand-navy mb-6">
                {content.aboutTitle}
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                {content.aboutDescription}
              </p>
              <div className="space-y-4">
                {content.aboutPoints.map((point, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Our Simple Process - 3 Steps */}
      <section className="py-20 md:py-32 bg-brand-navy-light">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              Our Simple Process
            </h2>
            <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto">
              We've streamlined the process to make protecting your brand simple and stress-free
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {content.processSteps.map((step, idx) => (
              <div key={idx} className="relative">
                <div className="bg-brand-navy-card border border-blue-900/30 rounded-xl p-8 h-full transition-all duration-300 hover:border-blue-500 hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] hover:-translate-y-2 group">
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {/* Connector Arrow (hidden on mobile, last item) */}
                {idx < content.processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-blue-500" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Pricing Section */}
      {content.showPricing && <Pricing />}

      {/* 5. Why Choose Us (Image on right) */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content on Left */}
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-brand-navy mb-6">
                {content.whyChooseTitle}
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                {content.whyChooseDescription}
              </p>
              <div className="space-y-4">
                {content.whyChoosePoints.map((point, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-gray-700 font-medium">{point}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Image on Right */}
            <div>
              <img
                src={content.whyChooseImage}
                alt={content.whyChooseTitle}
                className="rounded-2xl shadow-2xl w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 6. CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-base md:text-lg mb-8 text-white/90">
            Start your trademark registration today with our expert team
          </p>
          <Button
            onClick={() => openPopup(serviceKey)}
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 font-medium text-lg px-8 py-6 transition-transform hover:scale-105 shadow-xl"
          >
            Get Started Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* 7. Testimonials */}
      <Testimonials />

      {/* 8. FAQs */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-brand-navy mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              Find answers to common questions about our services
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {content.faqs.map((faq, idx) => (
              <AccordionItem 
                key={idx} 
                value={`item-${idx}`} 
                className="border border-gray-200 rounded-lg px-6 bg-gray-50"
              >
                <AccordionTrigger className="text-left font-semibold text-brand-navy hover:text-blue-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};